/**
 * HeaderProjectSwitcher.js
 *
 * Dual-dropdown component rendered in the AppBar (header). Allows the user to
 * quickly switch the active project and branch without leaving the current view.
 *
 * Data flow back to App.js:
 *   1. User picks a project -> handleProjectSelect fires -> calls onProjectChange(project)
 *      -> App.js `handleProjectChange` sets `selectedProject`, updates localStorage,
 *         and adds the project to the recent-projects list.
 *   2. User picks a branch  -> handleBranchSelect fires  -> calls onBranchChange(branchId)
 *      -> App.js `setCurrentBranch` updates the branch state, which is passed as
 *         `branchId` prop to whichever section view is currently rendered (PartsView,
 *         UnifiedCADView, RequirementsView, etc.).
 *
 * The branch selector is disabled when no project is selected.
 *
 * Props received (all supplied by AppContent in App.js):
 *   @param {Array}    projects         - Full list of project objects from the API.
 *   @param {Object}   selectedProject  - Currently selected project object, or null.
 *   @param {Function} onProjectChange  - Callback: (project | null) => void.
 *                                        Maps to App.js `handleProjectChange`.
 *   @param {Array}    branches         - Branch objects for the selected project.
 *   @param {string}   currentBranch    - branch_id of the currently active branch.
 *   @param {Function} onBranchChange   - Callback: (branchId) => void.
 *                                        Maps to App.js `setCurrentBranch`.
 *   @param {Array}    recentProjectIds - Up to 5 most-recently-used project IDs,
 *                                        sourced from localStorage via App.js state.
 */
import React from 'react';
import {
    Box,
    FormControl,
    Select,
    MenuItem,
    ListSubheader,
    Divider,
    Typography,
    Chip,
    useTheme
} from '@mui/material';
import { FolderOpen, AccountTree } from '@mui/icons-material';

const HeaderProjectSwitcher = ({
    projects = [],
    selectedProject,
    onProjectChange,
    branches = [],
    currentBranch,
    onBranchChange,
    recentProjectIds = []
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    /*
     * Resolve recent project IDs to full project objects so we can render
     * them with their names. filter(Boolean) removes any IDs that no longer
     * match a project in the list (e.g. deleted projects).
     */
    const recentProjects = recentProjectIds
        .map(id => projects.find(p => p.project_id === id))
        .filter(Boolean);

    /* Projects not in the recent list, displayed under the "All Projects" section. */
    const otherProjects = projects.filter(
        p => !recentProjectIds.includes(p.project_id)
    );

    /**
     * handleProjectSelect -- MUI Select onChange handler for the project dropdown.
     * Resolves the selected project_id back to a full project object before
     * passing it upstream. Selecting the empty value ("Select Project") sends null,
     * which clears the project context in App.js.
     */
    const handleProjectSelect = (event) => {
        const projectId = event.target.value;
        if (projectId === '') {
            onProjectChange(null);
        } else {
            const project = projects.find(p => p.project_id === projectId);
            onProjectChange(project);
        }
    };

    /**
     * handleBranchSelect -- MUI Select onChange handler for the branch dropdown.
     * Passes the raw branch_id string upstream to App.js.
     */
    const handleBranchSelect = (event) => {
        onBranchChange(event.target.value);
    };

    /* Resolve current branch object for display logic. */
    const currentBranchObj = branches.find(b => b.branch_id === currentBranch);
    const isMainBranch = currentBranchObj?.name === 'main';

    /* Shared styling for both Select inputs (theme-aware borders and sizing). */
    const selectStyles = {
        minWidth: 160,
        bgcolor: 'background.paper',
        borderRadius: 2,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#667eea',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#667eea',
            borderWidth: 1,
        },
        '& .MuiSelect-icon': {
            color: 'text.secondary',
        },
        '& .MuiSelect-select': {
            py: 0.875,
            fontSize: '0.875rem',
        }
    };

    /* Branch select inherits project styles but with a narrower min-width. */
    const branchSelectStyles = {
        ...selectStyles,
        minWidth: 140,
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 4 }}>
            {/* ---- Project Selector ---- */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Folder icon badge next to the dropdown */}
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(102, 126, 234, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <FolderOpen sx={{ color: '#667eea', fontSize: 18 }} />
                </Box>
                <FormControl size="small">
                    <Select
                        value={selectedProject?.project_id || ''}
                        onChange={handleProjectSelect}
                        displayEmpty
                        sx={selectStyles}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    maxHeight: 400,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                    mt: 0.5,
                                }
                            }
                        }}
                    >
                        {/* Placeholder option when no project is selected */}
                        <MenuItem value="">
                            <Typography color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                                Select Project
                            </Typography>
                        </MenuItem>

                        {/* "Recent" section header -- only shown when recent projects exist */}
                        {recentProjects.length > 0 && (
                            <ListSubheader
                                sx={{
                                    bgcolor: 'background.paper',
                                    lineHeight: '32px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                Recent
                            </ListSubheader>
                        )}

                        {/* Recent project items, each with a "recent" badge chip */}
                        {recentProjects.map((project) => (
                            <MenuItem key={`recent-${project.project_id}`} value={project.project_id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '0.875rem' }}>{project.name}</Typography>
                                    <Chip
                                        label="recent"
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: '0.6rem',
                                            bgcolor: 'rgba(102, 126, 234, 0.1)',
                                            color: '#667eea',
                                        }}
                                    />
                                </Box>
                            </MenuItem>
                        ))}

                        {/* Divider between recent and all projects sections */}
                        {recentProjects.length > 0 && otherProjects.length > 0 && (
                            <Divider sx={{ my: 0.5 }} />
                        )}

                        {/* "All Projects" section header */}
                        {otherProjects.length > 0 && (
                            <ListSubheader
                                sx={{
                                    bgcolor: 'background.paper',
                                    lineHeight: '32px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                All Projects
                            </ListSubheader>
                        )}

                        {/* Remaining projects (not in recent list) */}
                        {otherProjects.map((project) => (
                            <MenuItem key={project.project_id} value={project.project_id} sx={{ fontSize: '0.875rem' }}>
                                {project.name}
                            </MenuItem>
                        ))}

                        {/* Empty-state fallback when no projects exist at all */}
                        {projects.length === 0 && (
                            <MenuItem disabled>
                                <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    No projects available
                                </Typography>
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Box>

            {/* ---- Branch Selector ---- */}
            {/* Disabled when no project is selected (selectedProject === null). */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Branch icon badge -- color changes based on whether a project is selected */}
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: selectedProject ? 'rgba(102, 126, 234, 0.1)' : (isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)'),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <AccountTree sx={{ color: selectedProject ? '#667eea' : 'text.disabled', fontSize: 18 }} />
                </Box>
                <FormControl size="small">
                    <Select
                        value={currentBranch || ''}
                        onChange={handleBranchSelect}
                        displayEmpty
                        disabled={!selectedProject}
                        sx={{
                            ...branchSelectStyles,
                            /* Subtler background when disabled (no project selected) */
                            bgcolor: selectedProject ? 'background.paper' : (isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'),
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    borderRadius: 2,
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                    mt: 0.5,
                                }
                            }
                        }}
                    >
                        {/* Placeholder shown when disabled or no branch selected */}
                        <MenuItem value="" disabled>
                            <Typography color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                                {selectedProject ? 'Select Branch' : '--'}
                            </Typography>
                        </MenuItem>

                        {/* Branch list items -- populated by App.js via refreshBranches() */}
                        {branches.map((branch) => (
                            <MenuItem key={branch.branch_id} value={branch.branch_id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '0.875rem' }}>{branch.name}</Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

        </Box>
    );
};

export default HeaderProjectSwitcher;
